<template>
  <div>
    <div class="filters" v-if="isAdmin && (customTypes.length || showDeleted)">
      <v-switch
        :label="$t('base.show_deleted')"
        v-model="showDeleted"
      />
    </div>

    <table class="table table--bars" v-if="customTypes.length">
      <thead>
        <tr class="table__row table__row--head">
          <th class="table__cell">{{ $t('base.ingredient_type') }}</th>
          <th class="table__cell"></th>
        </tr>
      </thead>
      <v-table-body :cols="2" :empty="false" :loading="!loaded">
        <tr
          class="table__row"
          v-for="item in customTypes"
          :key="'custom-' + item.id"
          :class="{ 'table__row--deleted': item.deleted }"
        >
          <td class="table__cell">
            <span>{{ item.name | shortstr }}</span>
            <div v-if="item.deleted" class="type-badge type-badge--deleted">{{ $t('base.deleted') }}</div>
          </td>
          <td class="table__cell table__cell--action">
            <v-btn
              v-if="!item.deleted"
              icon
              @click="selectIngredientType(item.id)"
            >
              <v-icon font-size="1" icon="cog" />
            </v-btn>
          </td>
        </tr>
      </v-table-body>
    </table>
    
    <ui-pagination
      v-if="showPagination"
      v-model="page"
      :page-count="pageCount"
      @page-change="pageChangeHandler"
    />
    
    <div class="empty-state" v-if="loaded && !hasTypes">
      <p>{{ $t('base.no_ingredient_types') }}</p>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import paginationMixin from '@/mixins/pagination.mixin'
import api from '@/api'
import UiPagination from '@/components/ui/ui-pagination/UiPagination.vue'

export default {
  name: 'IngredientTypesTable',

  mixins: [paginationMixin],

  components: {
    UiPagination
  },

  data: () => ({
    loaded: false,
    showDeleted: false,
    userType: null
  }),

  async created () {
    try {
      const { data } = await api.account.get()
      this.userType = data.user_type
    } catch (error) {
      console.error('Failed to fetch user type:', error)
      this.userType = null
    }
  },

  async mounted () {
    const params = this.getParams()
    await this.setupPagination(api.ingredienttypes.list, params)

    this.loaded = true
  },

  computed: {
    ...mapGetters('ingredienttypes', [
      'ingredientTypes',
      'count'
    ]),

    customTypes () {
      return this.items || []
    },

    hasTypes () {
      return this.customTypes.length > 0
    },

    isAdmin () {
      return this.userType === 'admin'
    }
  },

  watch: {
    async showDeleted () {
      this.loaded = false
      const params = this.getParams()
      await this.setupPagination(api.ingredienttypes.list, params)
      this.loaded = true
    }
  },

  methods: {
    ...mapActions('ingredienttypes', [
      'loadIngredientTypes',
      'selectIngredientType'
    ]),

    getParams (page = null) {
      const params = {}

      if (!this.isAdmin) {
        params.deleted = false
      } else {
        params.deleted = this.showDeleted
      }

      if (this.$route.params.farm_id) {
        params.farm = this.$route.params.farm_id
      }

      if (page || this.page > 1) {
        params.page = page || this.page
      }

      return params
    },

    async loadDataList () {
      await this.loadIngredientTypes(this.getParams())
      this.loaded = true
    },

    async updateData () {
      await this.loadIngredientTypes(this.getParams())
      await this.setupPagination(api.ingredienttypes.list, this.getParams())
      this.loaded = true
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/sass/abstracts/_variables.scss';

.filters {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
  
  ::v-deep .v-switch {
    &__label {
      font-size: 1.4rem;
      font-weight: 500;
      color: #666;
    }
  }
}

.table__cell:not(:last-child) {
  width: 90%;
}

.table__cell:last-child {
  width: 10%;
  text-align: right !important;
}

.table__row--deleted {
  background: #ffebee;
  opacity: 0.7;
  
  .table__cell {
    color: #c62828;
  }
}

.type-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  vertical-align: middle;

  &--deleted {
    background: #c62828;
    color: #fff;
    margin-left: 4px;
  }
}

.empty-state {
  padding: 40px;
  text-align: center;
  color: #999;
}
</style>